/**
 * CouponRegisterContainer
 * --
 */
import React, { useState, useEffect, useReducer, useCallback } from 'react';
import CouponRegisterPresenter from './CouponRegisterPresenter';
import { useNavigate, useParams } from 'react-router-dom';
import MessageAlert from 'utils';
import {
  useGetShopInformations,
  useGetStores,
} from 'hooks/ShopInformationHooks';
import { useGetMenus } from 'hooks/MenuHooks';
import {
  useMapCreateCoupon,
  useMapCreateProductCoupon,
} from 'hooks/CouponHooks';
import { useGetAllCategories } from 'hooks/ProductCategoryHooks';
import { useGetProducts } from 'hooks/ProductHooks';
import { Spin } from 'antd';
import dayjs from 'dayjs';

const CouponRegisterContainer = () => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();
  const params = useParams();

  const registerType = React.useMemo(() => {
    return params.type ?? 'menu';
  }, [params]);

  /* ===== MUTATE ===== */
  // 메뉴 쿠폰 일괄 생성
  const { mapCreateCoupon } = useMapCreateCoupon();
  // 상품 쿠폰 일괄 생성
  const { mapCreateProductCoupon } = useMapCreateProductCoupon();

  const mutate = {
    menu: mapCreateCoupon,
    product: mapCreateProductCoupon,
  };

  /* ===== DATA ===== */
  // 스토어 전체 조회
  const { data: Stores } = useGetStores(null, {
    enabled: registerType === 'menu' ? true : false,
  });
  // 업체 전체 조회
  const { data: Shops } = useGetShopInformations(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      const filter = data?.filter((_item) => _item?.type !== 'STORE');

      return filter;
    },
    enabled: registerType === 'product' ? true : false,
    initialData: [],
  });
  // 메뉴 전체 조회
  const { data: Menus } = useGetMenus(
    {
      join_tables: '["product_category"]',
    },
    {
      enabled: registerType === 'menu' ? true : false,
    }
  );
  // 상품 전체 조회
  const { data: Products } = useGetProducts(
    {
      join_tables: '["shop_information", "product_category"]',
    },
    {
      enabled: registerType === 'product' ? true : false,
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        // 렌탈 상품 필터
        const filter = data?.filter(
          (_item) =>
            _item?.type !== 'RENTAL' && _item?.application_status === 'APPROVE'
        );

        return filter;
      },
      initialData: [],
    }
  );
  // 카테고리 전체 조회
  const { data: Categories } = useGetAllCategories(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      const filterCategory = data?.filter(
        (_item) => _item?.type === registerType?.toLocaleUpperCase()
      );
      return filterCategory;
    },
    initialData: [],
    enabled: !!registerType,
  });

  // console.log(Menus?.data);
  /* ===== STATE ===== */

  // 로딩
  const [isLoading, setIsLoading] = useState(false);
  // 정제된 데이터
  const [menuData, setMenuData] = React.useState([]);
  const [storeData, setStoreData] = React.useState([]);
  // 쿠폰 기본 정보
  const [couponValue, setCouponValue] = React.useState({
    coupon_name: '', // 쿠폰 이름
    coupon_code: '', // 쿠폰 코드
    coupon_content: {}, // 쿠폰 설명
    unavailable_store: '',
    note: '',
    benefit: '',
    standard: '',
    // coupon_issue_num: 0, // 발급 수량
    // minimum_price: 0, // 최소 사용 금액
    voucher_type: 'AMOUNT_DISCOUNT',
    start_date: null, // 시작일
    end_date: null, // 종료일
    // expiration_date: 30, // 만료 기간
    expiration_type: 'ROLLING', // 만료 타입 (ROLLING, FIXED)
    is_duplication: 'N', // 중복 사용 여부
    payment_type: 'MANUAL', // 지급 유형 (MANUAL, AUTOMATIC, DOWNLOAD, STAMP)
    coupon_type: 'MENU', // 쿠폰 타입 (MENU, FREQUENCY, MD, PRODUCT, DESERT, ALL)
    issue_event: null, // 발급 시점 (SIGNUP, FIRST_PURCHASE, REVIEW_POSTED, ORDER_COMPLETED, DELIVERY_COMPLETED)
    //TODO: 나중에 주석풀기
    is_reissue: 'N',
    per_user_limit: 1,
    is_used: 'Y',
  });
  const [policyValue, setPolicyValue] = React.useState({
    // 정책 정보
    policy_type: 'COUPON', // 정책 타입 (고정)
    // discount_rate: 0, // 할인율
    // reserve_rate: 0, // 적립율
    discount_type: 'W', // 할인 타입 (원: W, 률: R)
    reserve_type: 'W', // 적립 타입 (원: W, 률: R)
    commission_rate: 0, // 수수료율 (0 ~ 100)
    commission_type: 'R', // 수수료율 타입
    share_ratio: 0, // 분담 비율 (0 ~ 100, 기본값: 100)
    is_show: 'Y', // 표시 여부
  });
  const [storeValue, setStoreValue] = React.useState([]);
  const [menuValue, setMenuValue] = React.useState([]);
  // 전체 매장 선택
  const [isCheckedStoreAll, setIsCheckedStoreAll] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'TOGGLE':
          return !state;
        case 'RESET':
          return false;
        default:
          return state;
      }
    },
    false
  );
  // 전체 메뉴 선택
  const [isCheckedMenuAll, setIsCheckedMenuAll] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'TOGGLE':
          return !state;
        case 'RESET':
          return false;
        default:
          return state;
      }
    },
    false
  );
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  function generateRandomString(length = 16) {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  }

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = useCallback((t, v, o, callback = null) => {
    callback((prev) => {
      if (prev[t] === v) return prev;

      const newData = { ...prev, [t]: v };

      return newData;
    });
  }, []);

  /**
   * 체크박스 핸들러
   * --
   * @param { boolean } c
   * @param { object | array } d
   * @param { string } t
   */
  const handleStoreChecked = (c, d, t = null) => {
    if (t) {
      // 전체 선택인 경우
      setStoreValue(d);
    } else {
      // 단일 선택인 경우
      if (c) {
        setStoreValue((prev) => [...prev, d]);
      } else {
        setStoreValue((prev) => prev.filter((item) => item.key !== d.key));
      }
    }
  };
  const handleMenuChecked = (c, d, t = null) => {
    if (t) {
      // 전체 선택인 경우
      setMenuValue(d);
    } else {
      // 단일 선택인 경우
      if (c) {
        setMenuValue((prev) => [...prev, d]);
      } else {
        setMenuValue((prev) => prev.filter((item) => item.key !== d.key));
      }
    }
  };

  /**
   * 뒤로가기
   */
  const handleCancel = () => {
    navigate(-1);
  };

  /**
   * 쿠폰 등록
   * --
   */
  const handleSubmit = async () => {
    // 유효성 검사
    if (!handleValidateCheck()) return;

    let success = false;
    try {
      setIsLoading(true);

      const disabled_shop_list = storeValue?.map((item) => ({
        shop_information_id: item.shop_information_id,
      }));
      // console.log('disabled_shop_list');

      // 매장 정보 데이터 정제
      const couponCreateData = {
        coupon: {
          ...couponValue,
          coupon_content: {
            unavailable_store: couponValue?.unavailable_store, //대표자 명
            note: couponValue?.note, // 매장 연락처
            benefit: couponValue?.benefit, // 사업자등록번호
            standard: couponValue?.standard, // 영업시간
          },
          start_date: couponValue?.start_date
            ? dayjs(couponValue?.start_date).startOf('day').unix()
            : null,
          end_date: couponValue?.end_date
            ? dayjs(couponValue?.end_date).endOf('day').unix()
            : null,
          disabled_shop: disabled_shop_list ? disabled_shop_list : null,
          coupon_type:
            registerType === 'product' ? 'PRODUCT' : couponValue?.coupon_type,
        },
        product_policy: {
          ...policyValue,
          discount_rate:
            couponValue?.voucher_type === 'EXCHANGE'
              ? 0
              : policyValue?.discount_rate,
          discount_type:
            couponValue?.voucher_type === 'PERCENT_DISCOUNT' ? 'R' : 'W',
        },
        [`${registerType}_ids`]:
          couponValue?.coupon_type === 'ALL' ? [] : menuValue,
      };
      // 쿠폰 생성
      const { status } = await mutate[registerType](couponCreateData);

      // 데이터 업데이트 성공 시
      if (status === 200) {
        MessageAlert.success(`성공적으로 쿠폰이 등록되었습니다.`);
        // 값 초기화
        setCouponValue((prev) => ({ ...prev, t: {} }));
        setPolicyValue((prev) => ({ ...prev, t: {} }));

        success = true;
        return;
      } else {
        MessageAlert.warning('쿠폰을 등록하는데 실패하였습니다.');
      }
    } catch (err) {
      console.error(`[CREATE COUPON] ERROR : `, err);
      MessageAlert.error('쿠폰을 등록하는데 오류가 발생하였습니다.');
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);

        if (success) {
          window.location.reload();
        }
      }, 1000);
    }
  };

  /**
   * 유효성 검사
   * --
   */
  const handleValidateCheck = () => {
    // NOTE: 쿠폰 기본 정보 유효성 검사
    const {
      coupon_name,
      coupon_issue_num,
      minimum_price,
      voucher_type,
      payment_type,
      issue_event,
      expiration_type,
      expiration_date,
      end_date,
    } = couponValue;

    // 쿠폰명 입력 여부 체크
    if (!coupon_name) {
      MessageAlert.warning('쿠폰명을 입력해주세요.');
      return false;
    }
    // 발급수량 입력 여부 체크
    if (coupon_issue_num !== 0 && !coupon_issue_num) {
      MessageAlert.warning('전체 발급 가능 수량을 입력해주세요.');
      return false;
    }
    // minimum_price 입력 여부 체크
    if (minimum_price !== 0 && !minimum_price) {
      MessageAlert.warning('최소 사용 금액을 입력해주세요.');
      return false;
    }
    // voucher_type 입력 여부 체크
    if (!voucher_type) {
      MessageAlert.warning('바우처타입을 선택해주세요.');
      return false;
    }
    // 발급시점 입력 여부 체크
    if (payment_type === 'AUTOMATIC') {
      if (!issue_event) {
        MessageAlert.warning('발급시점을 입력해주세요.');
        return false;
      }
    }

    // NOTE: 정책 정보 유효성 검사
    const {
      // 정책 정보
      discount_rate,
      reserve_rate,
      max_discount_price,
    } = policyValue;

    // 할인율 입력 여부 체크
    if (couponValue?.voucher_type !== 'EXCHANGE') {
      if (discount_rate !== 0 && !discount_rate) {
        MessageAlert.warning('할인율/금액을 입력해주세요.');
        return false;
      }
    }
    // 적립율 입력 여부 체크
    if (reserve_rate !== 0 && !reserve_rate) {
      MessageAlert.warning('적립율/금액을 입력해주세요.');
      return false;
    }
    // 최대 할인 금액 입력 여부 체크
    if (max_discount_price !== 0 && !max_discount_price) {
      MessageAlert.warning('최대 할인 금액을 입력해주세요.');
      return false;
    }
    //발급일 기준 만료일자 체크
    if (expiration_type === 'ROLLING' && !expiration_date) {
      MessageAlert.warning('만료일자를 입력해주세요.');
      return false;
    }
    //발급일 기준 종료일자 체크
    if (expiration_type === 'FIXED' && !end_date) {
      MessageAlert.warning('쿠폰 종료일자을 입력해주세요.');
      return false;
    }
    // // share_ratio 여부 체크
    // if (!share_ratio) {
    //   MessageAlert.warning('분담 비율을 입력해주세요.');
    //   return false;
    // }

    return true;
  };

  /**
   * 상품 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setMenuValue((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setMenuValue((prev) => prev.filter((select) => select.key !== obj.key));
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setMenuValue(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = menuValue.filter((user) =>
          obj.some((ob) => ob.key === user.key)
        );

        setMenuValue(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = menuValue.length === obj.length;

        if (!isAll) {
          setMenuValue(obj);
        } else {
          setMenuValue([]);
        }
        return;
      default:
        return;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 쿠폰 랜덤번호 생성
   * --
   */
  useEffect(() => {
    const randomString = generateRandomString();
    setCouponValue((prev) => ({
      ...prev,
      coupon_code: randomString, // `coupon_code`에 랜덤 문자열 설정
    }));
  }, []);

  useEffect(() => {
    const newData = Stores?.data
      ?.filter(
        (item) =>
          item?.application_status === 'APPROVE' && item?.type === 'STORE'
      )
      .map((item) => ({
        ...item,
        key: item.shop_information_id,
        business_num: item.store_information.business_num,
      }));

    setStoreData(newData);
  }, [Stores]);

  /**
   * 데이터 정제
   * --
   */
  useEffect(() => {
    let newData = [];

    if (registerType === 'menu') {
      newData = Menus?.data
        ?.filter((item) => item?.product_category?.product_category_name) // product_category_name이 있는 데이터만 필터링
        .map((item) => ({
          ...item,
          key: item.menu_id,
          product_category_name: item.product_category.product_category_name, // product_category_name을 상위로 이동
          product_category: undefined, // product_category 제거
        }));
    } else {
      newData = Products?.map((_item) => ({
        ..._item,
        key: _item?.product_id,
        product_category_name: _item?.product_category?.product_category_name,
        product_category_id: _item?.product_category?.product_category_id,
        shop_information_name: _item?.shop_information?.shop_information_name,
      }));
    }

    setMenuData(newData);
  }, [Menus, Products, registerType]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <CouponRegisterPresenter
          storeData={storeData}
          menuData={menuData}
          couponValue={couponValue}
          policyValue={policyValue}
          storeValue={storeValue}
          menuValue={menuValue}
          setCouponValue={setCouponValue}
          setPolicyValue={setPolicyValue}
          setStoreValue={setStoreValue}
          setMenuValue={setMenuValue}
          onStoreChecked={handleStoreChecked}
          isCheckedStoreAll={isCheckedStoreAll}
          onStoreCheckedAll={setIsCheckedStoreAll}
          onMenuChecked={handleMenuChecked}
          isCheckedMenuAll={isCheckedMenuAll}
          onMenuCheckedAll={setIsCheckedMenuAll}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onChange={handleChange}
          // NOTE -
          registerType={registerType}
          onSelect={handleSelect}
          categories={Categories}
          Shops={Shops}
        />
      </Spin>
    </>
  );
};

export default CouponRegisterContainer;
